import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "../../helpers";
import { body } from "../typography";

const baseButtonClassName = cn([
  "inline-flex items-center justify-center rounded-full transition-all duration-150 ease-in-out",
  body({ size: "sm" }),
  "focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-blurple-600 focus-visible:outline-none disabled:cursor-not-allowed disabled:user-select-none",
]);

const buttonVariants = cva(baseButtonClassName, {
  variants: {
    variant: {
      primary: "bg-blurple-500 text-white",
      secondary: "bg-white text-blurple-500 border border-blurple-500",
      tertiary: "bg-white text-black border border-neutral-200",
      destructive: "bg-white text-red-700 border border-red-700",
      ghost: "bg-neutral-50/0 text-black",
      onColor: "bg-blurple-900 text-white",
    },
    size: {
      lg: "px-6 py-3.5 gap-2 h-14",
      md: "px-4 py-3 gap-2 h-12",
      sm: "px-4 py-3 gap-2 h-10",
      xs: "px-3 py-2 gap-1 h-8",
    },
    shape: {
      icon: "p-0",
      circle: "p-0",
    },
    isLoading: {
      true: "",
      false: "",
    },
    isFullWidth: {
      true: "w-full",
      false: "",
    },
    isFill: {
      true: "",
      false: "",
    },
  },
  compoundVariants: [
    {
      size: "lg",
      shape: "icon",
      className: "w-14 h-14",
    },
    {
      size: "md",
      shape: "icon",
      className: "w-12 h-12",
    },
    {
      size: "sm",
      shape: "icon",
      className: "w-10 h-10",
    },
    {
      size: "xs",
      shape: "icon",
      className: "w-8 h-8",
    },
    {
      size: "lg",
      shape: "circle",
      className: "w-14 h-14",
    },
    {
      size: "md",
      shape: "circle",
      className: "w-12 h-12",
    },
    {
      size: "sm",
      shape: "circle",
      className: "w-10 h-10",
    },
    {
      size: "xs",
      shape: "circle",
      className: "w-8 h-8",
    },
    {
      variant: "primary",
      isLoading: false,
      className:
        "hover:bg-blurple-600 active:bg-blurple-700 disabled:bg-blurple-300 disabled:text-white/80",
    },
    {
      variant: "secondary",
      isLoading: false,
      className:
        "hover:bg-blurple-50 active:bg-blurple-100  disabled:text-blurple-300 disabled:border-blurple-200",
    },
    {
      variant: "tertiary",
      isLoading: false,
      className:
        "hover:bg-neutral-50 active:bg-neutral-100  disabled:text-neutral-400 disabled:border-neutral-200",
    },
    {
      variant: "destructive",
      isLoading: false,
      className:
        "hover:bg-red-50 active:bg-red-100  disabled:text-red-300 disabled:border-red-200",
    },
    {
      variant: "ghost",
      isLoading: false,
      className:
        "hover:bg-neutral-50 active:bg-neutral-100  disabled:bg-white disabled:text-neutral-400",
    },
    {
      variant: "ghost",
      isLoading: true,
      className: "bg-white",
    },
    {
      variant: "tertiary",
      shape: "circle",
      className:
        "bg-neutral-50 hover:bg-neutral-100 active:bg-neutral-200 border-none",
    },
    {
      variant: "onColor",
      isLoading: false,
      className:
        "hover:bg-blurple-800 active:bg-blurple-700 disabled:bg-blurple-600 disabled:text-white/80",
    },
    {
      variant: "tertiary",
      isFill: true,
      className:
        "bg-neutral-100 hover:bg-neutral-200 active:bg-neutral-200 border-none",
    },
  ],
  defaultVariants: {
    variant: "primary",
    size: "md",
    isLoading: false,
    isFullWidth: false,
    isFill: false,
  },
});

const loaderIcon = cn("h-5 w-5 animate-spin");
const wrapper = cn("flex items-center justify-center gap-2");

type ButtonVariants = VariantProps<typeof buttonVariants>;
export type ButtonVariant = NonNullable<ButtonVariants["variant"]>;
export type ButtonSize = NonNullable<ButtonVariants["size"]>;

export { wrapper, loaderIcon, buttonVariants, baseButtonClassName };
